import request from '@/utils/request'
import {
  JSON_CONFIG,
} from '@/utils/helper.js'
import { addSignatureHeaders } from '@/utils/request-signer'
import { getDecryptionKey, isEncryptionEnabled, isSignatureEnabled } from '@/utils/runtime-config'
import { ERROR_CODE, failureOutcome } from '@/report/errors'

// 解密金鑰（runtime override 於地端 config.js 注入 → 否則環境變數）
const DECRYPTION_KEY = getDecryptionKey(process.env.VUE_APP_REPORT_DECRYPTION_KEY)
// 是否啟用回應加密（runtime override → 否則 build-time env；生產環境預設啟用）
const ENCRYPTION_ENABLED = isEncryptionEnabled(process.env.VUE_APP_ENABLE_REPORT_ENCRYPTION === 'true')
// 是否啟用請求簽章（與加密拆開對應後端兩把獨立開關；config.js 未設
// signatureEnabled 時跟隨 encryptionEnabled，雲端/舊 config 行為不變）
const SIGNATURE_ENABLED = isSignatureEnabled(process.env.VUE_APP_ENABLE_REPORT_ENCRYPTION === 'true')

/**
 * 將 Base64 字串轉換為 Uint8Array
 */
function base64ToUint8Array(base64) {
  const binaryString = atob(base64)
  const len = binaryString.length
  const bytes = new Uint8Array(len)
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i)
  }
  return bytes
}

/**
 * 解密報告數據（AES-GCM 模式，匹配後端 Go 實現）
 * 
 * 後端加密流程：
 * 1. 生成隨機 nonce (12 bytes for GCM)
 * 2. 使用 AES-GCM 加密: ciphertext = gcm.Seal(nonce, nonce, plaintext, nil)
 * 3. 返回 Base64(nonce + ciphertext)
 * 
 * @param {string} encryptedData - Base64 編碼的加密數據（包含 nonce + ciphertext）
 * @returns {object} 解密後的數據
 */
async function decryptReportData(encryptedData) {
  try {
    if (!DECRYPTION_KEY) {
      throw new Error('解密密鑰未設置（VUE_APP_REPORT_DECRYPTION_KEY）')
    }
    
    if (!encryptedData || typeof encryptedData !== 'string') {
      throw new Error(`無效的加密數據格式，收到: ${typeof encryptedData}`)
    }
    
    // 將密鑰從 Base64 轉換為 Uint8Array
    const keyBytes = base64ToUint8Array(DECRYPTION_KEY)
    
    // 將加密數據從 Base64 轉換為 Uint8Array
    const encryptedBytes = base64ToUint8Array(encryptedData)
    
    // GCM nonce 大小是 12 bytes
    const nonceSize = 12
    
    if (encryptedBytes.length < nonceSize + 16) {
      throw new Error(`加密數據太短（${encryptedBytes.length} bytes），至少需要 ${nonceSize + 16} bytes (nonce + ciphertext + tag)`)
    }
    
    // 提取 nonce（前 12 bytes）
    const nonce = encryptedBytes.slice(0, nonceSize)
    
    // 提取 ciphertext（nonce 之後的所有數據，包含 16 bytes auth tag）
    const ciphertext = encryptedBytes.slice(nonceSize)
    
    // 使用 Web Crypto API 解密（支持 AES-GCM）
    const key = await crypto.subtle.importKey(
      'raw',
      keyBytes,
      { name: 'AES-GCM' },
      false,
      ['decrypt']
    )
    
    const decryptedBytes = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: nonce,
        tagLength: 128 // GCM 標籤長度：128 bits (16 bytes)
      },
      key,
      ciphertext
    )
    
    // 將解密後的 ArrayBuffer 轉換為字串
    const decoder = new TextDecoder('utf-8')
    const decryptedText = decoder.decode(decryptedBytes)
    
    if (!decryptedText) {
      throw new Error('解密結果為空，可能是密鑰錯誤')
    }
    
    const jsonData = JSON.parse(decryptedText)
    
    console.log('[報告數據解密成功]', jsonData)
    
    return jsonData
  } catch (error) {
    console.error('[報告數據解密失敗]', {
      errorType: error.name,
      errorMessage: error.message,
      encryptedDataLength: encryptedData?.length,
      hasKey: !!DECRYPTION_KEY
    })
    
    // 提供更有幫助的錯誤訊息
    if (error.name === 'OperationError') {
      throw new Error(
        '數據解密失敗: 可能是密鑰不匹配或數據格式錯誤'
      )
    }
    
    throw new Error(`數據解密失敗: ${error.message}`)
  }
}

/**
 * 統一的請求處理函數
 * 
 * ⚠️ 重要：lang 參數格式
 * - 必須使用 URL query 中的原始格式（如 zh_TW，不是 zh-TW）
 * - 簽名計算會使用這個原始值，確保與後端驗證一致
 * 
 * @param {string} url - API URL
 * @param {string} token - 報告 token (reportId)
 * @param {string} lang - 語言參數（來自 URL query，如 'zh_TW'）
 * @param {object} [extraParams] - 額外 query 參數
 * @param {object} [options] - { timeout }：只對這一個請求的 axios 逾時（毫秒；不改 utils/request.js 的全域預設 90 秒——工單 17）
 * @returns {Promise} API 回應
 */
async function requestReportData(url, token, lang, extraParams, options = {}) {
  const params = { lang, ...(extraParams || {}) }
  
  // 只有在啟用請求簽章時才加入簽名（獨立於回應加密）
  // addSignatureHeaders 會使用 token 和 lang 生成簽名
  const headers = SIGNATURE_ENABLED
    ? addSignatureHeaders(JSON_CONFIG, token, lang)
    : JSON_CONFIG
  
  // 錯誤細節（報告改版工單 17）：validateStatus 全收，非 200 時 utils/request.js 照既有行為回原始 response
  // （帶 HTTP status）——不經它的 error 分支，所以不必改它的 reject 形狀。失敗一律丟出帶 reportOutcome
  // （src/report/errors.js 的 failureOutcome：status／errorCode／requestId／executionStatus／createdAt）的 Error。
  let response
  try {
    response = await request({
      url,
      method: 'get',
      headers,
      params,
      validateStatus: () => true,
      ...(options.timeout > 0 ? { timeout: options.timeout } : {})
    })
  } catch (error) {
    // 沒有收到回應（網路錯、逾時；request.js 回 { code: -1, … }）
    throw withReportOutcome(new Error(`[report] request failed: ${url}`), failureOutcome({ network: true }), error)
  }
  if (typeof response.status === 'number' && response.status !== 200) {
    throw withReportOutcome(
      new Error(`[report] HTTP ${response.status}: ${url}`),
      failureOutcome({ status: response.status, body: response.data })
    )
  }
  
  // 如果啟用加密
  if (ENCRYPTION_ENABLED) {
    // 檢查後端是否返回加密數據
    if (response.data && response.data.encrypted === true && response.data.payload) {
      // 後端返回加密格式: { encrypted: true, payload: "...", requestId }——requestId 在信封外層（工單 09）
      const envelopeRequestId = response.data.requestId
      try {
        response.data = await decryptReportData(response.data.payload)
      } catch (error) {
        console.error('[解密錯誤]', error.message)
        // 前端自生的 CLIENT_DECRYPT；requestId 取自加密信封外層（解密失敗時也有碼可查）
        throw withReportOutcome(error, failureOutcome({ status: 200, clientCode: ERROR_CODE.CLIENT_DECRYPT, requestId: envelopeRequestId }))
      }
      response.requestId = envelopeRequestId
    } else if (process.env.VUE_APP_SERVER === 'dev') {
      // 開發環境提示：後端可能還沒實現加密
      console.warn('[注意] 前端啟用了加密，但後端返回的數據不是加密格式')
    }
  }
  if (!response.data || typeof response.data !== 'object') {
    // 200 但不是 JSON 物件（例如閘道回 HTML）：當舊後端非契約錯誤（回報碼 HTTP200）
    throw withReportOutcome(new Error(`[report] unexpected body: ${url}`), failureOutcome({ status: 200 }))
  }
  // 成功回應的 requestId（未加密在頂層、加密在信封外層；工單 09）
  if (response.requestId === undefined) response.requestId = response.data.requestId
  
  return response
}

/** 把錯誤細節掛到 Error 上（store 讀 error.reportOutcome）；cause＝原本的 rejection（除錯用）。 */
function withReportOutcome(error, outcome, cause) {
  error.reportOutcome = outcome
  if (cause !== undefined) error.cause = cause
  return error
}

// noWait：要求後端略過「未完成就 sleep 最多 6 秒」（後端工單 09）；報告頁殼一律帶（spec Q38），
// 未完成改由頁殼的自動重試處理（src/report/retry-controller.js）。後端不認得時忽略，行為不變。
export async function getContestReportById (token, lang, { noWait = false } = {}) {
  return requestReportData(`/front/v1/report/${token}`, token, lang, noWait ? { noWait: 1 } : undefined)
}

export async function getRecommandById (token, lang) {
  return requestReportData(`/front/v1/report/${token}/discussion`, token, lang)
}

export async function getAIAnalysisById (token, lang) {
  return requestReportData(`/front/v1/report/${token}/aiAnalysis`, token, lang)
}

export async function getContestReportStatisticsById (token, lang) {
  return requestReportData(`/front/v1/report/${token}/statistics`, token, lang)
}

export async function getReportRecordsById (token, lang) {
  return requestReportData(`/front/v1/report/${token}/records`, token, lang)
}

// 動作歷史 /history 的逾時（報告改版工單 25 PR-B 站5 D2）：30 秒，不沿用全域 90 秒——後端 PDF 總預算 150 秒（pdf.go）且等網路靜止，
// 次要請求不能吃掉一半；API Gateway 整合逾時 29 秒，伺服器端拖過 30 秒本來就會變 504，所以這裡只多擋「用戶端網路黑洞」。
// 逾時＝沒有回應（outcome.network）→ store 記 failed／NETWORK，只降級比較列。
export const HISTORY_TIMEOUT_MS = 30000

// 動作歷史（報告改版工單 25；後端工單 12）：公開、與其他 front v1 同一條簽章＋限流鏈；回應可能加密（同信封）。
// 回應 { anchor, series:[{ analysisKey, points:[…] }], requestId }，降級時 series:[]＋reason。只給新渲染器的比較列用——
// 發不發、何時發由呼叫端依 src/report/compare-switch.js 決定；失敗一律只降級比較列（store 的 fetchHistory）。
export async function getReportHistoryById (token, lang) {
  return requestReportData(`/front/v1/report/${token}/history`, token, lang, undefined, { timeout: HISTORY_TIMEOUT_MS })
}

/**
 * 前端錯誤上報（後端工單 10）：POST /front/v1/report/:reportId/client-error，body 見 src/report/errors.js 的
 * clientErrorPayload（只有 errorCode／blockId／例外類型等，不含報告內容與個資）。與其他 front v1 請求同一組簽章標頭。
 * 一律靜默：端點不存在（舊後端 404）、被拒、網路錯都不丟錯、不重試、不 console——用 fetch 而不經 utils/request.js
 * （它在沒有回應時會 console.error）。回傳的 promise 永遠 resolve。
 */
export function postClientError (token, payload) {
  try {
    const headers = SIGNATURE_ENABLED ? addSignatureHeaders(JSON_CONFIG, token) : { ...JSON_CONFIG }
    const base = String(request.defaults.baseURL || '').replace(/\/+$/, '')
    return fetch(`${base}/front/v1/report/${token}/client-error`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload)
    }).then(() => undefined, () => undefined)
  } catch (error) {
    return Promise.resolve()
  }
}
